import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQueryWithReAuth } from '../../utils/baseQueryWithReAuth';
import { jobsAccountApi } from '../serviceJobsAccount';
import {
  IMyResume,
  IResumeContactRequest,
  IResumeContactRequestPage,
  IResumeContactRequestPreview,
  IResumeContactResponse,
  IResumeDatabaseAccess,
  IResumeDetail,
  IResumeEducation,
  IResumeFile,
  IResumeFileDownload,
  IResumeLookup,
  IResumeSearchRequest,
  IResumeSearchResponse,
  IResumeUnblockResponse,
  IResumeWorkHistory,
  IUpdateMyResume,
  ResumeContactResponseAction,
  ResumeVisibility,
} from './interfaces';

const invalidateAccountHome = async (queryFulfilled: PromiseLike<unknown>, dispatch: any) => {
  try {
    await queryFulfilled;
    dispatch(jobsAccountApi.util.invalidateTags(['AccountHome']));
  } catch {
    // Mutation state exposes the API error to the page.
  }
};

// The bounded history feeds page by descending ID and advertise the next cursor
// in the X-Next-Id response header, which BevNET.API exposes to this origin.
const readNextId = (meta: unknown): number | null => {
  const response = (meta as { response?: Response } | undefined)?.response;
  const header = response && response.headers ? response.headers.get('X-Next-Id') : null;
  if (!header) return null;
  const parsed = Number(header);
  return isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toContactRequestPage = (
  rows: IResumeContactRequest[],
  meta: unknown
): IResumeContactRequestPage => ({
  rows: rows || [],
  nextId: readNextId(meta),
});

export const resumeApi = createApi({
  reducerPath: 'serviceResume',
  baseQuery: baseQueryWithReAuth,
  tagTypes: [
    'MyResume',
    'ResumeAccess',
    'ResumeSearch',
    'ResumeDetail',
    'ResumeRequests',
  ],
  endpoints: (build) => ({
    getMyResume: build.query<IMyResume, void>({
      query: () => ({ url: '/Resumes/MyResume' }),
      providesTags: ['MyResume'],
    }),
    updateMyResume: build.mutation<IMyResume, IUpdateMyResume>({
      query: (body) => ({ url: '/Resumes/MyResume', method: 'PUT', body }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    saveResumeEducation: build.mutation<IResumeEducation, IResumeEducation>({
      query: (body) => ({ url: '/Resumes/ResumeEducation', method: 'POST', body }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    deleteResumeEducation: build.mutation<void, number>({
      query: (schoolId) => ({ url: `/Resumes/ResumeEducation/${schoolId}`, method: 'DELETE' }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    saveResumeWorkHistory: build.mutation<IResumeWorkHistory, IResumeWorkHistory>({
      query: (body) => ({ url: '/Resumes/ResumeWorkHistory', method: 'POST', body }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    deleteResumeWorkHistory: build.mutation<void, number>({
      query: (previousEmployerId) => ({ url: `/Resumes/ResumeWorkHistory/${previousEmployerId}`, method: 'DELETE' }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    uploadResumeFile: build.mutation<IResumeFile, File>({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);
        return { url: '/Resumes/ResumeFile', method: 'POST', body };
      },
      invalidatesTags: ['MyResume', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    updateResumeVisibility: build.mutation<IMyResume, ResumeVisibility>({
      query: (visibility) => ({
        url: '/Resumes/ResumeVisibility',
        method: 'PUT',
        body: { visibility },
      }),
      invalidatesTags: ['MyResume', 'ResumeSearch', 'ResumeDetail'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateAccountHome(queryFulfilled, dispatch),
    }),
    getResumeExpertises: build.query<IResumeLookup[], void>({
      query: () => ({ url: '/Resumes/Expertises' }),
    }),
    getResumeIndustrySectors: build.query<IResumeLookup[], void>({
      query: () => ({ url: '/Resumes/IndustrySectors' }),
    }),

    // Employer access state, terms, and per-listing introduction allowances.
    getResumeDatabaseAccess: build.query<IResumeDatabaseAccess, void>({
      query: () => ({ url: '/Resumes/ResumeDatabaseAccess' }),
      providesTags: ['ResumeAccess'],
      extraOptions: { suppressErrorNotification: true },
    }),
    acceptResumeDatabaseTerms: build.mutation<IResumeDatabaseAccess, void>({
      query: () => ({ url: '/Resumes/AcceptResumeDatabaseTerms', method: 'POST' }),
      invalidatesTags: ['ResumeAccess', 'ResumeSearch', 'ResumeDetail'],
      extraOptions: { suppressErrorNotification: true },
    }),

    searchResumes: build.query<IResumeSearchResponse, IResumeSearchRequest>({
      query: (params) => ({ url: '/Resumes/SearchResumes', params }),
      providesTags: ['ResumeSearch'],
      extraOptions: { suppressErrorNotification: true },
    }),
    getResumeDetail: build.query<IResumeDetail, string>({
      query: (rguid) => ({ url: `/Resumes/ResumeDetail/${rguid}` }),
      providesTags: (_result, _error, rguid) => [{ type: 'ResumeDetail', id: rguid }],
      extraOptions: { suppressErrorNotification: true },
    }),
    // The employer never receives a file URL in the detail payload. Downloading
    // mints a short-lived, separately audited URL at click time.
    mintEmployerResumeFile: build.mutation<IResumeFileDownload, string>({
      query: (rguid) => ({ url: `/Resumes/MintEmployerResumeFile/${rguid}`, method: 'POST' }),
      extraOptions: { suppressErrorNotification: true },
    }),

    requestResumeContact: build.mutation<IResumeContactRequest, {
      rguid: string;
      classifiedId: number;
      message: string;
    }>({
      query: ({ rguid, ...body }) => ({
        url: `/Resumes/RequestResumeContact/${rguid}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { rguid }) => [
        'ResumeRequests',
        'ResumeAccess',
        { type: 'ResumeDetail', id: rguid },
      ],
      extraOptions: { suppressErrorNotification: true },
    }),

    // Anonymous candidate response from the emailed token.
    getResumeContactRequestPreview: build.query<IResumeContactRequestPreview, string>({
      query: (requestGuid) => ({ url: `/Resumes/ResumeContactRequest/${requestGuid}` }),
      extraOptions: { suppressErrorNotification: true },
    }),
    respondResumeContactRequest: build.mutation<IResumeContactResponse, {
      requestGuid: string;
      action: ResumeContactResponseAction;
      reason?: string;
    }>({
      query: ({ requestGuid, action, reason }) => ({
        url: `/Resumes/RespondResumeContactRequest/${requestGuid}`,
        method: 'POST',
        body: reason ? { action, reason } : { action },
      }),
      invalidatesTags: ['ResumeRequests'],
      extraOptions: { suppressErrorNotification: true },
    }),

    // Employer request history, newest first, paged by descending ID.
    getEmployerResumeContactRequests: build.query<IResumeContactRequestPage, {
      beforeId?: number;
      pageSize?: number;
    }>({
      query: ({ beforeId, pageSize = 25 }) => ({
        url: '/Resumes/EmployerResumeContactRequests',
        params: beforeId ? { beforeId, pageSize } : { pageSize },
      }),
      transformResponse: toContactRequestPage,
      providesTags: ['ResumeRequests'],
    }),

    // Compatibility shape used by Account Home: the newest actionable rows only.
    getSeekerResumeContactRequests: build.query<IResumeContactRequest[], void>({
      query: () => ({ url: '/Resumes/SeekerResumeContactRequests' }),
      providesTags: ['ResumeRequests'],
    }),
    // Full candidate-owned history including declined, expired, and revoked rows.
    getSeekerResumeContactHistory: build.query<IResumeContactRequestPage, {
      beforeId?: number;
      pageSize?: number;
    }>({
      query: ({ beforeId, pageSize = 25 }) => ({
        url: '/Resumes/SeekerResumeContactRequests',
        params: beforeId
          ? { includeHistory: true, beforeId, pageSize }
          : { includeHistory: true, pageSize },
      }),
      transformResponse: toContactRequestPage,
      providesTags: ['ResumeRequests'],
    }),

    // {id} is the contact-request id; both return the updated row.
    blockSeekerResumeContactRequest: build.mutation<IResumeContactRequest, number>({
      query: (id) => ({ url: `/Resumes/BlockSeekerResumeContactRequest/${id}`, method: 'POST' }),
      invalidatesTags: ['ResumeRequests'],
      extraOptions: { suppressErrorNotification: true },
    }),
    reportSeekerResumeContactRequest: build.mutation<IResumeContactRequest, {
      id: number;
      reason: string;
    }>({
      query: ({ id, reason }) => ({
        url: `/Resumes/ReportSeekerResumeContactRequest/${id}`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['ResumeRequests'],
      extraOptions: { suppressErrorNotification: true },
    }),
    // {id} is the blockId from the history row. Unblocking permits a future
    // introduction; it never restores an old approval.
    unblockResumeEmployer: build.mutation<IResumeUnblockResponse, number>({
      query: (blockId) => ({ url: `/Resumes/UnblockResumeEmployer/${blockId}`, method: 'POST' }),
      invalidatesTags: ['ResumeRequests'],
      extraOptions: { suppressErrorNotification: true },
    }),
  }),
});

export const {
  useGetMyResumeQuery,
  useUpdateMyResumeMutation,
  useSaveResumeEducationMutation,
  useDeleteResumeEducationMutation,
  useSaveResumeWorkHistoryMutation,
  useDeleteResumeWorkHistoryMutation,
  useUploadResumeFileMutation,
  useUpdateResumeVisibilityMutation,
  useGetResumeExpertisesQuery,
  useGetResumeIndustrySectorsQuery,
  useGetResumeDatabaseAccessQuery,
  useAcceptResumeDatabaseTermsMutation,
  useSearchResumesQuery,
  useGetResumeDetailQuery,
  useMintEmployerResumeFileMutation,
  useRequestResumeContactMutation,
  useGetResumeContactRequestPreviewQuery,
  useRespondResumeContactRequestMutation,
  useGetEmployerResumeContactRequestsQuery,
  useGetSeekerResumeContactRequestsQuery,
  useGetSeekerResumeContactHistoryQuery,
  useBlockSeekerResumeContactRequestMutation,
  useReportSeekerResumeContactRequestMutation,
  useUnblockResumeEmployerMutation,
} = resumeApi;

export * from './errors';
export * from './interfaces';
